// BLE transport — scan, connect, write, listen.
//
// Port of the Flutter ble_controller.dart, over @capacitor-community/bluetooth-le.
// Native only; on web every call is a no-op that reports "unsupported" rather
// than throwing, so the desktop build never touches it.
//
// This is what makes Wi-Fi onboarding possible in the React app at all. A
// WebView cannot join a device's setup AP — but it CAN speak BLE through this
// plugin, and the Smartify firmware provisions over BLE, so the whole flow ports
// cleanly.
//
// TRANSPORT ONLY. Authentication, the handshake and the session live in
// bleSession.js; anything that sends a COMMAND goes through there, because a
// device rejects everything until authenticated.

import { BleClient, numbersToDataView, ScanMode } from '@capacitor-community/bluetooth-le';
import {
  BLE_NAME_PREFIX, SERVICE_UUID, CHAR_UUID, WRITE_WITHOUT_RESPONSE,
  SCAN_TIMEOUT_MS, CONNECT_TIMEOUT_MS, REQUESTED_MTU,
  decodeNotification, encode, parseServiceData,
} from './bleConfig';
import { isNativePlatform } from '../platform';

// BLE IS MOBILE ONLY. Desktop is a browser with no BLE plugin, and every entry
// point here is gated on this rather than on a try/catch, so the desktop bundle
// can import this module without ever reaching the native layer.
//
// ⚠️ A WEB BLUETOOTH PATH WAS BUILT HERE AND THEN REMOVED — do not rebuild it
// without reading this first. The plugin does ship a web implementation, and it
// does work on localhost, so the idea is not wrong on paper. It was removed
// because on the desktop it is not usable in practice:
//
//   • `navigator.bluetooth` exists only in a SECURE CONTEXT. The deployed site
//     is plain HTTP, so the whole feature was dead everywhere except a dev
//     server — i.e. exactly where it is least needed.
//   • The web has no scan, only a browser-drawn CHOOSER: one device at a time,
//     no RSSI, no advertised assigned/unassigned state. Half the bench screen's
//     value is that list.
//   • Chrome and Edge only; Firefox and Safari implement none of it.
//
// The bench is the installed app on a phone. That is where the hardware is.
export const bleSupported = () => isNativePlatform();

// Kept as a distinct name because the CALLERS read better for it: the automatic
// paths (offline control, ensureControlLink) are asking "can BLE be used
// without anyone having asked for it", which is a different question from "does
// this build have a radio" even while the answer happens to be the same.
export const bleAutoSupported = () => isNativePlatform();

// ── GATT operations are SERIALISED ─────────────────────────────────────────
//
// One outstanding write/read at a time, per the spec's platform notes:
// "Concurrent operations fail silently on many devices." Silently is the
// problem — an overlapping write is not an error you can catch, it is a command
// that never happened, which during a handshake means an auth that never
// completes for no visible reason.
//
// A single chain is enough: BLE traffic here is a handshake followed by
// occasional commands, never throughput.
let gattChain = Promise.resolve();

function serialise(fn) {
  const run = gattChain.then(fn, fn);
  // Keep the chain alive after a rejection, but let the caller still see it.
  gattChain = run.then(() => undefined, () => undefined);
  return run;
}

let initialised = false;

export async function initBle() {
  if (!bleSupported()) throw new Error('Bluetooth is only available in the app');
  if (initialised) return;
  await BleClient.initialize({ androidNeverForLocation: true });
  initialised = true;
}

// ── THE ADAPTER BEING OFF IS THE FIFTH SILENT FAILURE ──────────────────────
//
// It belongs on the list at the top of CLAUDE.md with the other four, and it is
// the one that actually reached a tester: **a scan with Bluetooth switched off
// throws nothing and finds nothing.**
//
// Read the plugin's Android source and it is plain why:
//
//   • `initialize()` requests PERMISSIONS and nothing else — BluetoothLe.kt
//     `fun initialize` ends at `requestPermissionForAliases(...)`. It never
//     looks at whether the adapter is on.
//   • every scan/connect entry point guards with `assertBluetoothAdapter()`,
//     which rejects only when `bluetoothAdapter == null` — i.e. when the phone
//     HAS NO BLUETOOTH HARDWARE. An adapter that exists and is switched off
//     passes that check.
//
// So `requestLEScan` resolves, the callback is never invoked, and the scan runs
// its full 15-second timeout to an empty list. Indistinguishable on screen from
// "no devices are advertising" — which is why it was debugged as a firmware
// problem.
//
// Nothing may scan or connect without passing through here first.

export const BLE_OFF = 'BLE_OFF';
export const BLE_UNSUPPORTED = 'BLE_UNSUPPORTED';

export class BleUnavailableError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'BleUnavailableError';
    this.code = code;
  }
}

export const isBleOffError = (e) => e && e.code === BLE_OFF;

/** Is the radio switched on? `false` — never a throw — when BLE is unavailable. */
export async function isBleEnabled() {
  if (!bleSupported()) return false;
  try {
    await initBle();
    return await BleClient.isEnabled();
  } catch (e) {
    return false;
  }
}

/**
 * Ask Android to turn Bluetooth on — the system dialog, not a settings trip.
 *
 * ⚠️ ANDROID ONLY, and `requestEnable` NOT `enable`. `enable()` is deprecated
 * and REJECTS on Android 13+ (SDK 33), which is most of the fleet — the plugin
 * says so in its own docs. On iOS both are `unavailable`: an app may not turn
 * the user's radio on, so there the only honest move is to say so and let them
 * do it. That is why this returns a boolean rather than throwing: "we could not
 * ask" is a normal outcome, not an error.
 *
 * -> true if Bluetooth is on when this returns.
 */
export async function requestBleEnable() {
  if (!bleSupported()) return false;
  try {
    await initBle();
    if (await BleClient.isEnabled()) return true;
    // Rejects if the user declines the system dialog — a decision, not a fault.
    await BleClient.requestEnable();
    return await BleClient.isEnabled();
  } catch (e) {
    return false;
  }
}

/** Android: jump to the system Bluetooth settings. No-op elsewhere. */
export async function openBleSettings() {
  try { await BleClient.openBluetoothSettings(); } catch (e) { /* unavailable */ }
}

/**
 * Watch the radio being switched on or off, so a screen can clear its own
 * warning without the user having to press anything.
 *
 * Returns an unsubscribe function. The callback fires with the CURRENT state
 * first — a screen mounting while Bluetooth is already off must be told so, and
 * a notification-only API would leave it looking fine until something changed.
 */
export function watchBleEnabled(onChange) {
  let live = true;
  if (!bleSupported()) {
    onChange(false);
    return () => { live = false; };
  }
  (async () => {
    try {
      await initBle();
      const now = await BleClient.isEnabled();
      if (live) onChange(now);
      await BleClient.startEnabledNotifications((value) => {
        if (live) onChange(value);
      });
    } catch (e) {
      if (live) onChange(false);
    }
  })();
  return () => {
    live = false;
    BleClient.stopEnabledNotifications().catch(() => {});
  };
}

/**
 * The gate every scan and connect passes through.
 *
 * Throws a typed `BleUnavailableError` so a caller can tell "Bluetooth is off"
 * from "the device did not answer" — two failures that look identical on screen
 * and have completely different fixes.
 */
export async function ensureBleReady() {
  if (!bleSupported()) {
    throw new BleUnavailableError(BLE_UNSUPPORTED, 'Bluetooth is only available in the app');
  }
  await initBle();
  if (!(await BleClient.isEnabled())) {
    throw new BleUnavailableError(BLE_OFF, 'Bluetooth is switched off on this phone');
  }
}

// Scan for Smartify devices for SCAN_TIMEOUT_MS, calling onDevice as each is
// seen. Resolves when the scan stops.
export async function scanForDevices(onDevice) {
  // Before anything else: a scan with the radio off resolves normally and finds
  // nothing. See ensureBleReady.
  await ensureBleReady();
  // deviceId -> { named } so a device can be re-emitted once its name arrives.
  const seen = new Map();

  await BleClient.requestLEScan({
    // Filter on the SERVICE UUID, which is in the advertisement proper and is
    // the reliable filter on both platforms. The name is only in the scan
    // response, so filtering by name alone loses devices whose response has not
    // arrived yet.
    services: [SERVICE_UUID],
    // ⚠️ MUST BE TRUE, AND THIS IS LOAD-BEARING — it was false, and that made
    // working devices invisible.
    //
    // The plugin reports the name from `result.device.name`
    // (BluetoothLe.kt getScanResult), which is Android's CACHED name for the
    // address, not the name parsed out of this scan record. It is null until
    // Android has resolved one, and the plugin then omits the field entirely.
    //
    // With allowDuplicates false, DeviceScanner.kt delivers exactly ONE
    // callback per address for the whole scan:
    //     if (allowDuplicates || isNew) { scanResultCallback?.invoke(result) }
    //
    // So if the first sighting landed before Android had a name -- which is
    // what happens on a cold cache, and toggling Bluetooth clears that cache --
    // the old code below dropped it on the name check and never got a second
    // callback. The device stayed invisible for the full 15 s while advertising
    // perfectly: confirmed against the firmware, which reported `advertising`
    // over MQTT while an independent scanner saw 49 adverts in 20 s.
    //
    // Intermittent by nature: once Android has cached the name it is present
    // immediately and everything looks fine, which is exactly what made this
    // hard to pin down.
    allowDuplicates: true,
    // ⚠️ LOW_LATENCY IS ACTIVE SCANNING. 31 bytes cannot hold both the service
    // data and the name, so the firmware puts the name in the SCAN RESPONSE —
    // which only exists if the scan actively asks for it. A passive scan sees
    // the service UUID and no name, and a name-based filter then matches
    // nothing, indistinguishable from no devices present.
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    const id = result.device?.deviceId;
    if (!id) return;

    // ⚠️ DO NOT REJECT ON THE NAME. `services: [SERVICE_UUID]` is applied by
    // the platform before we are called, so anything arriving here is already
    // advertising 0x00FF and is a Smartify device by definition. The old
    // `if (!name.startsWith(BLE_NAME_PREFIX)) return;` filtered nothing that
    // the UUID had not filtered already, and silently discarded real devices
    // whose name Android had not resolved yet.
    const name = result.localName || result.device?.name || '';
    const named = Boolean(name);

    // Emit on the first sighting, and once more if the name turns up later so
    // the caller can fill in uniqueNumber. Nothing after that.
    const prev = seen.get(id);
    if (prev && (prev.named || !named)) return;
    seen.set(id, { named });

    // The advertised state/owner_tag, so the list can say "new" or "mine"
    // without connecting to anything.
    const raw = result.serviceData && result.serviceData[SERVICE_UUID];
    onDevice({
      deviceId: id,
      name,
      uniqueNumber: name.startsWith(BLE_NAME_PREFIX)
        ? (name.slice(BLE_NAME_PREFIX.length) || null)
        : null,
      rssi: result.rssi,
      service: raw ? parseServiceData(raw) : null,
    });
  });

  await new Promise((r) => { setTimeout(r, SCAN_TIMEOUT_MS); });
  try { await BleClient.stopLEScan(); } catch (e) { /* already stopped */ }
}

// ⚠️ NEVER LET THIS BLOCK. stopLEScan is awaited on the path to a connect, and
// on some Android stacks it does not settle when no scan is running — or when
// one is mid-flight. An await that never returns there is invisible: the connect
// never starts, nothing is logged because the first log line comes after it, and
// the caller's `busy` flag stays set so the device list stops responding to
// taps. The whole screen looks dead while the radio is perfectly fine.
//
// Capping it is safe. The worst case of giving up early is a scan that keeps
// running for its own timeout, which costs battery and nothing else.
const STOP_SCAN_TIMEOUT_MS = 2000;

export async function stopScan() {
  try {
    await Promise.race([
      BleClient.stopLEScan(),
      new Promise((resolve) => { setTimeout(resolve, STOP_SCAN_TIMEOUT_MS); }),
    ]);
  } catch (e) { /* already stopped */ }
}

export async function connect(deviceId, onDisconnect) {
  await ensureBleReady();
  // ⚠️ THE TIMEOUT HAS TO BE PASSED. bleConfig has declared CONNECT_TIMEOUT_MS
  // since this file was written and nothing ever used it, so every connect ran
  // on the plugin's 10 s default.
  //
  // That is tight for THIS hardware. The ESP8684 is a single-core part sharing
  // one antenna between Wi-Fi and BLE, so a node that is already associated and
  // talking MQTT answers a connection request in the gaps between Wi-Fi
  // activity. Ten seconds is enough when the radio is idle and not always
  // enough when it is not — which reads as "it finds the device but connecting
  // does nothing", intermittently, and only on a device that is working.
  await BleClient.connect(deviceId, onDisconnect, { timeout: CONNECT_TIMEOUT_MS });
  // Negotiate the MTU immediately after connecting and BEFORE any write.
  // Skipping this truncates AUTH (37 bytes) and WIFI (up to 101) against a
  // 20-byte default payload, with no error — see REQUESTED_MTU.
  //
  // Best-effort: iOS negotiates on its own and rejects the call, and some
  // Android stacks refuse. A refusal is not fatal on its own — the firmware
  // reassembles chunked writes — so it is logged rather than thrown.
  try {
    await BleClient.requestMtu(deviceId, REQUESTED_MTU);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[BLE] MTU negotiation refused; long writes may be chunked');
  }
}

export async function disconnect(deviceId) {
  try { await BleClient.disconnect(deviceId); } catch (e) { /* ignore */ }
}

// Subscribe to the notify characteristic. Returns an unsubscribe function.
export async function listen(deviceId, onText) {
  await BleClient.startNotifications(deviceId, SERVICE_UUID, CHAR_UUID, (value) => {
    const text = decodeNotification(value);
    if (text) onText(text);
  });
  return async () => {
    try {
      await BleClient.stopNotifications(deviceId, SERVICE_UUID, CHAR_UUID);
    } catch (e) { /* ignore */ }
  };
}

export async function write(deviceId, text) {
  const data = encode(text);
  return serialise(async () => {
    if (WRITE_WITHOUT_RESPONSE) {
      try {
        await BleClient.writeWithoutResponse(deviceId, SERVICE_UUID, CHAR_UUID, data);
        return;
      } catch (e) {
        // Fall through — some stacks reject write-without-response mid-session.
      }
    }
    await BleClient.write(deviceId, SERVICE_UUID, CHAR_UUID, data);
  });
}

// A GATT read of the characteristic returns the current status string (§4).
export async function readStatus(deviceId) {
  return serialise(async () => {
    const value = await BleClient.read(deviceId, SERVICE_UUID, CHAR_UUID);
    return decodeNotification(value);
  });
}

// Exposed for callers that already hold raw bytes.
export { numbersToDataView };
